import { useEffect, useRef } from "react";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";

// ParsedRecordsContext fetches once, when its provider first mounts (login
// time) — it has no way of knowing a new upload has since finished
// processing. UploadHistoryContext is the one place that *does* know (it
// polls upload status), so this hook watches it for a processing -> complete
// transition and, when one happens, tells ParsedRecordsContext to refetch.
// Medications/DiagnosticReports/Visits are pure derived selectors over
// ParsedRecordsContext's data (see those files), so refreshing it alone is
// enough — they recompute automatically via useMemo once `records` updates.
// Without this, the only way to see new data is a full page reload, which
// remounts every provider from scratch. Every new fetch-once context added to
// this app should be wired in here the same way — see the app-wide
// instant-reactivity preference this hook exists to satisfy.
export function useAutoRefreshClinicalData() {
  const { zipUploads } = useUploadHistory();
  const { refresh: refreshParsedRecords } = useParsedRecords();
  const prevStatuses = useRef<Record<string, string>>({});

  useEffect(() => {
    let justCompleted = false;
    for (const entry of zipUploads) {
      const prevStatus = prevStatuses.current[entry.id];
      prevStatuses.current[entry.id] = entry.status;
      if (prevStatus && prevStatus !== "complete" && entry.status === "complete") {
        justCompleted = true;
      }
    }
    if (justCompleted) {
      refreshParsedRecords();
    }
  }, [zipUploads, refreshParsedRecords]);
}
